import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHapticFeedback } from '@/hooks/useMobileGestures';

interface PullToRefreshProps {
  children: React.ReactNode;
  onRefresh: () => Promise<void>;
  threshold?: number;
  disabled?: boolean;
}

const PullToRefresh: React.FC<PullToRefreshProps> = ({
  children,
  onRefresh,
  threshold = 80,
  disabled = false
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isTriggered, setIsTriggered] = useState(false);
  const startY = useRef(0);
  const { lightImpact, mediumImpact } = useHapticFeedback();

  const handleTouchStart = (e: React.TouchEvent) => {
    if (disabled || window.scrollY > 0) return;
    startY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (disabled || isRefreshing || window.scrollY > 0) return;

    const currentY = e.touches[0].clientY;
    const distance = Math.max(0, currentY - startY.current);
    
    if (distance > 0) {
      e.preventDefault();
      setPullDistance(distance);
      
      const shouldTrigger = distance >= threshold;
      if (shouldTrigger !== isTriggered) {
        setIsTriggered(shouldTrigger);
        lightImpact();
      }
    }
  };

  const handleTouchEnd = async () => {
    if (disabled || isRefreshing) return;

    if (isTriggered && pullDistance >= threshold) {
      setIsRefreshing(true);
      mediumImpact();
      
      try {
        await onRefresh();
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        setIsRefreshing(false);
      }
    }

    setPullDistance(0);
    setIsTriggered(false);
  };

  const refreshProgress = Math.min(pullDistance / threshold, 1);
  const iconRotation = refreshProgress * 180;

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className="relative overflow-hidden"
    >
      {/* Pull indicator */}
      <AnimatePresence>
        {(pullDistance > 0 || isRefreshing) && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="absolute top-0 left-0 right-0 z-10 flex items-center justify-center bg-background/95 backdrop-blur-sm border-b border-border"
            style={{ height: Math.min(pullDistance, threshold * 1.5) }}
          >
            <div className="flex flex-col items-center space-y-2 p-4">
              <div className={cn(
                "relative transition-colors duration-200",
                isTriggered ? "text-primary" : "text-muted-foreground"
              )}>
                {isRefreshing ? (
                  <RefreshCw className="h-6 w-6 animate-spin" />
                ) : (
                  <ChevronDown 
                    className="h-6 w-6 transition-transform duration-200"
                    style={{ transform: `rotate(${iconRotation}deg)` }}
                  />
                )}
              </div>
              
              <div className="text-center">
                <p className={cn(
                  "text-sm font-medium transition-colors duration-200",
                  isTriggered ? "text-primary" : "text-muted-foreground"
                )}>
                  {isRefreshing 
                    ? "Actualisation..." 
                    : isTriggered 
                      ? "Relâchez pour actualiser" 
                      : "Tirez pour actualiser"
                  }
                </p>
                
                {/* Progress indicator */}
                <div className="mt-2 w-20 h-1 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${refreshProgress * 100}%` }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      <motion.div
        animate={{ 
          y: isRefreshing ? threshold : Math.min(pullDistance, threshold * 1.5)
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {children}
      </motion.div>
    </div>
  );
};

export default PullToRefresh;